import React from 'react';
import { Card, Space, Typography, Empty, Alert, Button } from 'antd';
import type { Company } from '../../types/company';
import { trpc } from '../../lib/trpc';
import { AddonSummaryCard } from './AddonSummaryCard';
import { AddonAddForm } from './AddonAddForm';
import { AddonList } from './AddonList';
import { useLatestCancellationRequest } from '../../hooks/useCompanyState';

const { Text } = Typography;

interface AddonManagementPanelProps {
  company: Company;
  isManager: boolean;
  onSwitchToTab?: (tabKey: string) => void; // [v2.1] 追加
}

export const AddonManagementPanel: React.FC<AddonManagementPanelProps> = ({
  company,
  isManager,
  onSwitchToTab  // [v2.1] 追加
}) => {
  const { data: addOnsData, isLoading } = trpc.subscriptionAddon.listAddOns.useQuery();
  const { latestRequest } = useLatestCancellationRequest(company.id);

  // 解約申請中かどうか（subscriptionStatus または latestRequest で判定）
  const isCancellationPending = company.subscriptionStatus === 'canceling' || latestRequest?.status === 'pending';

  if (isLoading) {
    return <div>追加オプション情報を読み込み中...</div>;
  }

  const hasStripeSubscription = Boolean(company.stripeSubscriptionId);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* [v2.1 新規] 解約申請中の警告 */}
      {isCancellationPending && (
        <Alert
          message="解約申請中のため、追加オプションの追加・変更はできません"
          description={
            <Space direction="vertical" size="small">
              <Text>
                現在、解約申請が受理されています。
                {(company.cancelEffectiveDate || latestRequest?.effectiveDate) && (
                  <>
                    <strong>{new Date(company.cancelEffectiveDate ?? latestRequest?.effectiveDate ?? '').toLocaleDateString('ja-JP')}</strong> をもってサービスが停止されます。
                  </>
                )}
              </Text>
              <Text>
                追加オプションの追加・変更を行う場合は、先に解約を取り消してください。
              </Text>
            </Space>
          }
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => onSwitchToTab?.('cancellation')}>
              解約タブを開く
            </Button>
          }
        />
      )}

      <AddonSummaryCard addOns={addOnsData?.addOns} />

      {/* [v2.1 変更] 解約申請中は追加オプション追加を非表示 */}
      {isManager && hasStripeSubscription && !isCancellationPending && (
        <Card title="追加オプションを追加">
          <AddonAddForm />
        </Card>
      )}

      {!hasStripeSubscription && isManager && (
        <Card>
          <Empty
            description={
              <Space direction="vertical" size="small">
                <Text>追加オプションを追加するには、先にStripe契約を開始してください。</Text>
                <Text type="secondary">基本契約タブからサブスクリプションを開始できます。</Text>
              </Space>
            }
          />
        </Card>
      )}

      <Card title="現在の追加オプション">
        <AddonList
          addOns={addOnsData?.addOns}
          isManager={isManager}
          hasStripeSubscription={hasStripeSubscription}
          isCancellationPending={isCancellationPending}
        />
      </Card>
    </Space>
  );
};

export default AddonManagementPanel;
